import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, Loader2, FileText, Compass, Home, Layers, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API, AUTH_URL } from '../config';

const AdvancedUpload = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [propertyType, setPropertyType] = useState('home');
  const [orientation, setOrientation] = useState('north');
  const [analyzing, setAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    'Reading floor plan image',
    'Running local room segmentation',
    'Matching labels and geometry',
    'Preparing editor overlay',
  ];

  const propertyTypes = [
    { value: 'home', label: 'Home/Apartment', icon: Home },
    { value: 'office', label: 'Office', icon: Layers },
    { value: 'commercial', label: 'Commercial Space', icon: Layers },
  ];

  const orientations = [
    { value: 'north', label: 'North' },
    { value: 'south', label: 'South' },
    { value: 'east', label: 'East' },
    { value: 'west', label: 'West' },
  ];

  const processSelectedFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);

    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(selectedFile);
      return;
    }

    if (selectedFile.type === 'application/pdf') {
      setPreview('pdf');
    }
  };

  const handleAnalyze = async () => {
    if (!file || !user) return;

    setAnalyzing(true);
    setCurrentStep(0);

    const safetyTimeout = setTimeout(() => {
      setAnalyzing(false);
      alert('Analysis taking too long. Please verify the drawing quality and retry.');
    }, 120000);

    const progressTimers = [
      setTimeout(() => setCurrentStep(1), 700),
      setTimeout(() => setCurrentStep(2), 1800),
      setTimeout(() => setCurrentStep(3), 2800),
    ];

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result.split(',')[1];

        try {
          const response = await axios.post(
            `${API}/vastu/advanced-detect-rooms`,
            {
              file_content_base64: base64String,
              orientation,
            },
            { withCredentials: true }
          );

          clearTimeout(safetyTimeout);
          progressTimers.forEach(clearTimeout);
          setCurrentStep(3);

          const resultData = response.data || {};
          const detectedRooms = Array.isArray(resultData.rooms) ? resultData.rooms : [];

          navigate('/smart-editor', {
            state: {
              floorPlanBase64: base64String,
              propertyType,
              orientation,
              detectedRooms,
              detectionMeta: resultData,
            },
          });
        } catch (error) {
          clearTimeout(safetyTimeout);
          progressTimers.forEach(clearTimeout);
          console.error('Advanced detection error:', error);
          alert('Advanced room detection failed. Opening editor without detected rooms.');
          navigate('/smart-editor', {
            state: {
              floorPlanBase64: base64String,
              propertyType,
              orientation,
              detectedRooms: [],
              detectionMeta: {
                detection_status: 'failed',
                errors: ['Advanced detection request failed before analysis completed.'],
              },
            },
          });
        }
      };

      reader.onerror = () => {
        clearTimeout(safetyTimeout);
        progressTimers.forEach(clearTimeout);
        setAnalyzing(false);
        alert('Failed to read floor plan file. Please try again.');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      clearTimeout(safetyTimeout);
      progressTimers.forEach(clearTimeout);
      console.error(error);
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#232323]" />
          <p className="text-[#353535]">Checking login status...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-3xl font-bold mb-4 text-[#232323]">Login Required</h2>
          <p className="text-[#353535] mb-6">Please log in to use advanced floor plan analysis.</p>
          <a
            href={AUTH_URL ? `${AUTH_URL}/?redirect=${encodeURIComponent(window.location.href)}` : '/login'}
            className="btn-primary"
          >
            Login with Google
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 text-[#232323]">Advanced Floor Plan Analysis</h1>
          <p className="text-[#353535]">
            Use the local custom room detector for precise room shapes and editable labels.
          </p>
        </div>

        <div className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-200 flex gap-3 text-blue-900 text-sm">
          <Compass className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Best for CAD-style 2D plans:</span> The advanced flow uses local YOLO first, then geometry cleanup, so it is faster and more reliable for your current floor plan tests.
          </div>
        </div>

        {analyzing ? (
          <div className="bg-white p-8 rounded-xl text-center border border-[#E5E5E5] shadow-sm max-w-xl mx-auto">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-[#232323]" />
            <p className="text-lg font-semibold mb-2 text-[#232323]">Preparing editor overlay...</p>
            <p className="text-sm text-[#353535] mb-6">Detected rooms will open directly in the editor.</p>
            <div className="space-y-3 text-left">
              {steps.map((step, index) => (
                <div key={step} className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      index < currentStep
                        ? 'bg-emerald-500'
                        : index === currentStep
                          ? 'bg-[#232323] animate-pulse'
                          : 'bg-gray-200'
                    }`}
                  />
                  <span className={`text-sm ${index <= currentStep ? 'text-[#232323]' : 'text-[#999999]'}`}>
                    {step}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div
                className="border-2 border-dashed border-[#999999] rounded-xl p-12 text-center bg-white hover:border-[#4D4D4D] transition-colors cursor-pointer"
                onDrop={(event) => {
                  event.preventDefault();
                  processSelectedFile(event.dataTransfer.files[0]);
                }}
                onDragOver={(event) => event.preventDefault()}
                onClick={() => document.getElementById('advancedFileInput').click()}
              >
                {preview ? (
                  <div className="space-y-4">
                    {preview === 'pdf' ? (
                      <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-200 max-w-sm mx-auto">
                        <FileText className="w-14 h-14 text-[#4D4D4D] mb-3" />
                        <span className="font-semibold text-[#232323] truncate max-w-[250px]">{file.name}</span>
                        <span className="text-xs text-[#777777] mt-1">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                      </div>
                    ) : (
                      <img src={preview} alt="Preview" className="max-h-72 mx-auto rounded-lg border border-gray-200" />
                    )}
                    <p className="text-sm text-[#353535]">Click or drop to replace file</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <UploadIcon className="w-16 h-16 mx-auto text-[#999999]" />
                    <p className="text-lg text-[#232323]">Drag & drop your floor plan here</p>
                    <p className="text-sm text-[#353535]">or click to browse files</p>
                    <p className="text-xs text-[#999999]">Supports: PNG, JPG, PDF</p>
                  </div>
                )}
                <input
                  id="advancedFileInput"
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  className="hidden"
                  onChange={(event) => processSelectedFile(event.target.files[0])}
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-[#E5E5E5] p-6 rounded-xl shadow-sm">
                <h3 className="font-bold text-[#232323] mb-4 flex items-center gap-2">
                  <Compass className="w-5 h-5 text-[#4D4D4D]" />
                  Configuration
                </h3>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#232323] mb-3">Property Type</label>
                    <div className="flex flex-col gap-2">
                      {propertyTypes.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            onClick={() => setPropertyType(type.value)}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                              propertyType === type.value
                                ? 'border-[#232323] bg-[#232323] text-white'
                                : 'border-[#999999] bg-white text-[#232323] hover:border-[#4D4D4D]'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {type.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#232323] mb-3">Main Entrance Direction</label>
                    <div className="grid grid-cols-2 gap-2">
                      {orientations.map((dir) => (
                        <button
                          key={dir.value}
                          onClick={() => setOrientation(dir.value)}
                          className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                            orientation === dir.value
                              ? 'border-[#232323] bg-[#232323] text-white'
                              : 'border-[#999999] bg-white text-[#232323] hover:border-[#4D4D4D]'
                          }`}
                        >
                          {dir.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAnalyze}
                disabled={!file}
                className={`btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 ${
                  !file ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {file ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Analyze & Open Editor
                  </>
                ) : (
                  'Upload floor plan to continue'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedUpload;
